/**
 * « Cibles citées » — the Vue d'ensemble card for the brand's Cibles, all axes at once
 * (2026-10-14). Replaces the Contenu / Produits sub-tabs each BP axis's « Tableau de
 * bord » used to carry: one card instead of three copies of the same view.
 *
 *  - ONE tab per Cibles category the brand actually holds (Établissements, Produits,
 *    Contenus, Personnes), from `/targets/summary` — a tab for an empty referential is a
 *    dead end the user has to open to discover. Produits = `produit` + `collection`.
 *  - Each tab opens on the GLOBAL citation volume, stacked by source (Image, Autorité,
 *    Recommandation, Conversations): `…/dashboard/target-citations`. Counts, not rates,
 *    so the layers add up and the top edge is the total.
 *  - Produits, Contenus and Personnes add the detail cards (`BPTargetsPanel`) under an
 *    axis switch: Global (the three axes merged by target id, `axis=all`) or one axis.
 *    Établissements has no detail cards yet — chart only.
 *
 * Window = the page's period, through `resolveBpRange` (inclusive span, 730-day cap).
 * Past 90 days everything reads the written monthly tier (`BPTOPM`), chart and detail
 * alike, so the whole card is true over two years.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Chip,
  MenuItem,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type {
  BPTargetCitationsResponse,
  BPTargetCitationsSource,
  BPTargetsAxis,
  TargetCategory,
  TargetsManifest,
} from '@traaker/shared';
import { api } from '../../services/api';
import { TrendLine, VIZ, fmtInt, setVizLocale, type TrendLineSeries } from '../../components/viz';
import BPTargetsPanel from '../../components/brand/BPTargetsPanel';
import { resolveBpRange } from '../../components/brand/bpRange';
import { catCount, tagRegistryOf } from '../targets/CATEGORIES';
import CardShell from './CardShell';
import { COLORS } from './colors';
import { RANGE_DAYS, type DateRange } from './dashboardData';

type CatTab = 'etablissement' | 'produit' | 'contenu' | 'personne';
const CAT_TABS: CatTab[] = ['etablissement', 'produit', 'contenu', 'personne'];

/** Bottom layer first — the stack order IS the legend order. */
const SOURCES: { key: BPTargetCitationsSource; labelKey: string; color: string }[] = [
  { key: 'narrative', labelKey: 'dashboard.axis.narrative', color: COLORS.narrative },
  { key: 'authority', labelKey: 'dashboard.axis.authority', color: COLORS.authority },
  { key: 'referral', labelKey: 'dashboard.axis.referral', color: COLORS.referral },
  { key: 'conversations', labelKey: 'dashboard.targets.sourceConversations', color: VIZ.series[3] },
];

const AXIS_TABS: { value: BPTargetsAxis; labelKey: string }[] = [
  { value: 'all', labelKey: 'dashboard.targets.axisAll' },
  { value: 'narrative', labelKey: 'dashboard.axis.narrative' },
  { value: 'authority', labelKey: 'dashboard.axis.authority' },
  { value: 'referral', labelKey: 'dashboard.axis.referral' },
];

const MS_PER_DAY = 86_400_000;

export interface TargetCitationsCardProps {
  brandId: string;
  range: DateRange;
  brandCreatedAt?: string;
}

export default function TargetCitationsCard({ brandId, range, brandCreatedAt }: TargetCitationsCardProps) {
  const { t, i18n } = useTranslation();
  setVizLocale(i18n.language);

  const [manifest, setManifest] = useState<TargetsManifest | null>(null);
  const [manifestLoaded, setManifestLoaded] = useState(false);
  const [tab, setTab] = useState<CatTab | null>(null);
  const [produitCat, setProduitCat] = useState<TargetCategory>('produit');
  const [axis, setAxis] = useState<BPTargetsAxis>('all');
  // Cibles tag IDS (uint16 from the manifest registry) — the detail cards' only filter.
  const [targetTags, setTargetTags] = useState<number[]>([]);
  const [data, setData] = useState<BPTargetCitationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    api
      .get<TargetsManifest>(`/brands/${brandId}/targets/summary`)
      .then((m) => setManifest(m ?? null))
      .catch(() => setManifest(null))
      .finally(() => setManifestLoaded(true));
  }, [brandId]);

  const produitOnly = catCount(manifest, 'produit');
  const collections = catCount(manifest, 'collection');
  const counts: Record<CatTab, number> = {
    etablissement: catCount(manifest, 'etablissement'),
    produit: produitOnly + collections,
    contenu: catCount(manifest, 'contenu'),
    personne: catCount(manifest, 'personne'),
  };
  const tabs = CAT_TABS.filter((c) => counts[c] > 0);
  // A VALUE, not an index: the tab set depends on the manifest. A tab that stops
  // existing (referential emptied) falls back to the first one that does.
  const activeTab = tab && tabs.includes(tab) ? tab : (tabs[0] ?? null);
  /** `produit` by default, unless the brand only loaded catalogue categories. */
  const effectiveProduitCat: TargetCategory = produitOnly === 0 && collections > 0 ? 'collection' : produitCat;
  const category: TargetCategory | null =
    activeTab === 'produit' ? effectiveProduitCat : activeTab;

  /** The page's period as an inclusive BP window; « Tout » runs from the brand's creation. */
  const span = useMemo(() => {
    const created = brandCreatedAt ? Date.parse(brandCreatedAt) : NaN;
    const days =
      RANGE_DAYS[range] ?? (Number.isFinite(created) ? Math.ceil((Date.now() - created) / MS_PER_DAY) + 1 : 730);
    return resolveBpRange({ kind: 'relative', days: Math.max(1, days) });
  }, [range, brandCreatedAt]);

  useEffect(() => {
    if (!category) return;
    const params = new URLSearchParams({ category, from: span.from, to: span.to });
    setLoading(true);
    let cancelled = false;
    const timer = setTimeout(() => {
      api
        .get<BPTargetCitationsResponse>(`/brands/${brandId}/brand-perception/dashboard/target-citations?${params}`)
        .then((resp) => {
          if (cancelled) return;
          setData(resp ?? null);
          setLoadError('');
        })
        .catch((err: any) => {
          if (cancelled) return;
          setData(null);
          setLoadError(err?.message || t('bpDash.loadError'));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, category, span.from, span.to]);

  const tagRegistry = useMemo(() => tagRegistryOf(manifest), [manifest]);
  const tagOptions = useMemo(() => [...tagRegistry.keys()], [tagRegistry]);
  const tagName = (id: number) => tagRegistry.get(id) ?? t('bpTargets.tagRetired', { id: String(id) });

  const fmtBucket = (key: string) => {
    if (key.length === 7) {
      return new Date(`${key}-01T00:00:00Z`).toLocaleDateString(i18n.language, {
        month: 'short',
        year: '2-digit',
        timeZone: 'UTC',
      });
    }
    const day = new Date(`${key}T00:00:00Z`).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
    return data?.grain === 'week' ? t('bpDash.weekOf', { date: day }) : day;
  };

  const series: TrendLineSeries[] = SOURCES.filter((s) => data?.series[s.key]).map((s) => ({
    key: s.key,
    label: t(s.labelKey),
    color: s.color,
    points: data!.series[s.key]!,
  }));

  const notes: string[] = [];
  if (data?.availableFrom && data.caveats.includes('window_precedes_target_data')) {
    notes.push(
      t('dashboard.targets.availableFrom', {
        date: new Date(`${data.availableFrom}T00:00:00Z`).toLocaleDateString(i18n.language, { timeZone: 'UTC' }),
      }),
    );
  }
  if (data?.partialBuckets?.length) notes.push(t('dashboard.targets.partial'));
  if (span.spanClamped) notes.push(t('dashboard.targets.clamped'));

  // ---- render ------------------------------------------------------------

  if (!manifestLoaded) {
    return (
      <CardShell title={t('dashboard.targets.title')} dotColor={COLORS.geo}>
        <Skeleton variant="rounded" height={280} />
      </CardShell>
    );
  }
  if (!activeTab || !manifest) {
    return (
      <CardShell
        title={t('dashboard.targets.title')}
        dotColor={COLORS.geo}
        link={{ label: t('dashboard.targets.setUp'), to: '/geo-strategy?cibles=produit#cibles' }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {t('dashboard.targets.noTargets')}
        </Typography>
      </CardShell>
    );
  }

  const hasDetail = activeTab !== 'etablissement';

  return (
    <CardShell
      title={t('dashboard.targets.title')}
      dotColor={COLORS.geo}
      link={{ label: t('dashboard.targets.manage'), to: `/geo-strategy?cibles=${category}#cibles` }}
    >
      <Tabs
        value={activeTab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        sx={{
          mb: 2,
          minHeight: 44,
          borderBottom: `1px solid ${VIZ.border}`,
          '& .MuiTabs-indicator': { height: 2, bgcolor: COLORS.geo },
          '& .MuiTab-root': {
            minHeight: 44,
            px: 1.75,
            fontSize: 14.5,
            fontWeight: 500,
            textTransform: 'none',
            color: VIZ.muted,
            '&:hover': { color: VIZ.ink },
          },
          '& .MuiTab-root.Mui-selected': { color: COLORS.geo, fontWeight: 600 },
        }}
      >
        {tabs.map((c) => (
          <Tab
            key={c}
            value={c}
            label={
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                {t(`cibles.tab.${c}`)}
                <Box
                  component="span"
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    px: 0.875,
                    py: '1px',
                    borderRadius: '10px',
                    bgcolor: c === activeTab ? 'rgba(124,58,237,0.10)' : '#F1F1F4',
                    color: c === activeTab ? COLORS.geo : VIZ.muted,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {fmtInt(counts[c])}
                </Box>
              </Box>
            }
          />
        ))}
      </Tabs>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: VIZ.ink }}>
          {t('dashboard.targets.chartTitle')}
        </Typography>
        {/* Only offered when the brand holds both halves of Produits; scopes the chart AND the detail. */}
        {activeTab === 'produit' && produitOnly > 0 && collections > 0 && (
          <TextField
            select
            size="small"
            label={t('bpTargets.filterCategory')}
            value={effectiveProduitCat}
            onChange={(e) => setProduitCat(e.target.value as TargetCategory)}
            sx={{ minWidth: 180, '& .MuiInputBase-root': { minHeight: 36 }, '& .MuiInputBase-input': { fontSize: 13 } }}
          >
            <MenuItem value="produit">
              {t('cibles.tab.produit')} ({fmtInt(produitOnly)})
            </MenuItem>
            <MenuItem value="collection">
              {t('cibles.tab.collection')} ({fmtInt(collections)})
            </MenuItem>
          </TextField>
        )}
      </Box>

      {loading ? (
        <Skeleton variant="rounded" height={260} />
      ) : loadError ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {loadError}
        </Typography>
      ) : (
        <TrendLine
          stacked
          labels={(data?.buckets ?? []).map(fmtBucket)}
          series={series}
          format={fmtInt}
          height={260}
          totalLabel={t('dashboard.targets.total')}
          emptyNote={t('dashboard.targets.empty')}
          unmeasuredNote={t('bpDash.notMeasured')}
        />
      )}
      <Typography sx={{ fontSize: 11.5, lineHeight: 1.5, color: VIZ.muted, mt: 1 }}>
        {[t('dashboard.targets.volumeHint'), ...notes].join(' ')}
      </Typography>

      {hasDetail && category && (
        <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${VIZ.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
            <ToggleButtonGroup value={axis} exclusive size="small" onChange={(_, v) => v && setAxis(v)}>
              {AXIS_TABS.map((a) => (
                <ToggleButton key={a.value} value={a.value} sx={{ py: 0.5, px: 1.5, fontSize: 12.5, textTransform: 'none' }}>
                  {t(a.labelKey)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Autocomplete<number, true>
              multiple
              size="small"
              disabled={tagOptions.length === 0}
              options={tagOptions}
              value={targetTags}
              onChange={(_, v) => setTargetTags(v)}
              getOptionLabel={tagName}
              renderTags={(value, getTagProps) =>
                value.map((id, i) => <Chip size="small" label={tagName(id)} {...getTagProps({ index: i })} key={id} />)
              }
              renderInput={(params) => (
                <TextField {...params} label={t('bpTargets.filterTargetTags')} placeholder={t('bpTargets.allTargetTags')} />
              )}
              sx={{ minWidth: 220, maxWidth: 360, ml: 'auto' }}
            />
          </Box>
          <Typography sx={{ fontSize: 11, lineHeight: 1.5, color: '#444', mb: 2 }}>
            {axis === 'all' ? `${t('dashboard.targets.axisAllHint')} ` : ''}
            {t('bpTargets.scopeHint')}
          </Typography>
          <BPTargetsPanel
            key={`${brandId}:${axis}:${category}`}
            brandId={brandId}
            axis={axis}
            kind={activeTab}
            category={category}
            manifest={manifest}
            from={span.from}
            to={span.to}
            spanClamped={span.spanClamped}
            targetTags={targetTags}
          />
        </Box>
      )}
    </CardShell>
  );
}
